import {skipToken} from '@tanstack/react-query';
import {useQuery, useMutation, useQueryClient, QueryClient} from '@tanstack/react-query';
import Report from '~/report/data/Report';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {
    PdfCreativeProfile,
    PdfReportCreate,
    ReportCreate,
    ReportDuplicate,
    ReportItemCreate,
    ReportItemRemove,
    ReportItemUpdate,
    ReportList,
    ReportQuery,
    ReportRemove,
    ReportRemoveMany,
    ReportSearch,
    ReportShareMany,
    ReportUpdate
} from './graphqlQueries';
import {CreativeReportItemUpsertInput, ReportItem} from '~/report/data/ReportItemDefinitionTypes';
import {mapCreativeDefinitionToReportApi} from '~/report/convertCreativeDefinitionToFilter';
import {
    ReportCreateInput,
    ReportItemRemoveInput,
    ReportItemV2CreateInput,
    ReportListInput,
    ReportSearchInput,
    ReportShareManyInput,
    ReportUpdateInput,
    ReportItemV2UpdateInput
} from '~/graphql-react-query/graphql';
import {createPollingGenerator} from 'bigdatr-style';

function mapReportItemInputForApi<T extends ReportItemV2CreateInput | ReportItemV2UpdateInput>(
    input: T
): T {
    if (input.type !== 'AdvertisingCreative' || !input.definitionV2) {
        return input;
    }

    return {
        ...input,
        definitionV2: mapCreativeDefinitionToReportApi(
            input.definitionV2 as CreativeReportItemUpsertInput
        )
    };
}

const reportListQueryKey = 'reportList';
const reportQueryKeys = {
    report: (reportId: string) => ['report', reportId] as const,
    allReportLists: () => [reportListQueryKey] as const,
    reportList: (listParams: ReportListInput) =>
        [
            reportListQueryKey,
            listParams.country,
            listParams.filter,
            listParams.orderBy,
            listParams.pagination
        ] as const
};

//
// single report CRUD

export function useFetchReport(reportId: string) {
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        queryKey: reportQueryKeys.report(reportId),
        queryFn: async () => {
            const response = await request(ReportQuery, {id: reportId});
            const rawReport = response.reportV2?.report;
            if (!rawReport) throw new Error('Report not returned from api');

            const report = new Report({
                ...rawReport,
                // we shot ourselves in the foot years ago when we decided to return report items as
                // a generic {[key: string]: any} type in the graphql schema. this type error was
                // hidden for years - ideally it is fixed in the api layer
                items: rawReport.items as ReportItem[]
            });

            return report;
        }
    });
}

export function useCreateReport() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: ReportCreateInput) => {
            const response = await request(ReportCreate, {input});
            const rawReport = response.reportV2?.reportCreate;
            if (!rawReport) throw new Error('Report not returned from api');

            const report = new Report({
                ...rawReport,
                items: rawReport.items as ReportItem[]
            });
            return report;
        },
        onSuccess: () => {
            // force a refetch of the list after creation
            queryClient.invalidateQueries({queryKey: reportQueryKeys.allReportLists()});
        }
    });
}

export function useUpdateReport() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: ReportUpdateInput) => {
            const response = await request(ReportUpdate, {input});
            const rawReport = response.reportV2?.reportUpdate;
            if (!rawReport) throw new Error('Report not returned from api');

            const report = new Report({
                ...rawReport,
                items: rawReport.items as ReportItem[]
            });
            return report;
        },
        onSuccess: (updatedReport) => {
            // update the cached report after update
            queryClient.setQueryData(reportQueryKeys.report(updatedReport.id), updatedReport);
            // force a refetch of the list after updating
            queryClient.invalidateQueries({queryKey: reportQueryKeys.allReportLists()});
        }
    });
}

export function useRemoveReport() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const response = await request(ReportRemove, {id});
            return response.reportV2?.reportRemove;
        },
        onSuccess: (_, deletedReportId) => {
            // update the cached report after update
            queryClient.setQueryData(reportQueryKeys.report(deletedReportId), undefined);
            // force a refetch of the list after deletion
            queryClient.invalidateQueries({queryKey: reportQueryKeys.allReportLists()});
        }
    });
}

export function useDuplicateReport() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const response = await request(ReportDuplicate, {id});
            const rawReport = response.reportV2?.reportDuplicate;
            if (!rawReport) throw new Error('Report not returned from api');

            const report = new Report({
                ...rawReport,
                items: rawReport.items as ReportItem[]
            });
            return report;
        },
        onSuccess: () => {
            // force a refetch of the list after duplication
            queryClient.invalidateQueries({queryKey: reportQueryKeys.allReportLists()});
        }
    });
}

//
// bulk reports CRUD

export function useListReports(input: ReportListInput) {
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        queryKey: reportQueryKeys.reportList(input),
        queryFn: async () => {
            const response = await request(ReportList, {input});
            const rawResponse = response.reportV3?.reportList;
            if (!rawResponse) throw new Error('Reports not returned from api');

            return {
                ...rawResponse,
                items: rawResponse.items.map((rawReport) => new Report(rawReport))
            };
        }
    });
}

export function useRemoveManyReports() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: {ids: string[]; listParams: ReportListInput}) => {
            const response = await request(ReportRemoveMany, {ids: input.ids});
            const rawReports = response.reportV2?.reportRemoveMany;
            if (!rawReports) throw new Error('Reports not returned from api');

            return rawReports.map((rawReport) => new Report(rawReport));
        },
        onSuccess: (updatedReports, variables) => {
            updateReportsInListCache({
                updatedReports,
                listInput: variables.listParams,
                queryClient
            });
        }
    });
}

export function useShareManyReports() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            updateData: ReportShareManyInput;
            listParams: ReportListInput;
        }) => {
            const response = await request(ReportShareMany, {input: input.updateData});
            const rawReports = response.reportV2?.reportShareMany;
            if (!rawReports) throw new Error('Reports not returned from api');

            return rawReports.map((rawReport) => new Report(rawReport));
        },
        onSuccess: (updatedReports, variables) => {
            updateReportsInListCache({
                updatedReports,
                listInput: variables.listParams,
                queryClient
            });
        }
    });
}

//
// report item CRUD

export function useCreateReportItem() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: ReportItemV2CreateInput) => {
            const response = await request(ReportItemCreate, {
                input: mapReportItemInputForApi(input)
            });
            const rawReportItem = response.reportV2?.reportItemCreate;
            if (!rawReportItem) throw new Error('Report item not returned from api');
            return {
                ...rawReportItem,
                definitionV2: rawReportItem.definitionV2 as ReportItem['definitionV2']
            };
        },
        onSuccess: (createdReportItem, mutationVariables) => {
            // update single report cache to include the newly created report item
            queryClient.setQueryData<Report>(
                reportQueryKeys.report(mutationVariables.reportId),
                (report) => {
                    if (!report) return;

                    return new Report({
                        ...report,
                        updatedAt: new Date().toISOString(),
                        items: [...report.items, createdReportItem as ReportItem]
                    });
                }
            );
        }
    });
}

export function useRemoveReportItem() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: ReportItemRemoveInput) => {
            const response = await request(ReportItemRemove, {input});
            return response.reportV2?.reportItemRemove;
        },
        onSuccess: (isReportRemoved, mutationVariables) => {
            // update single report cache to remove the deleted report item
            queryClient.setQueryData<Report>(
                reportQueryKeys.report(mutationVariables.reportId),
                (oldReport) => {
                    if (!oldReport) return;
                    return new Report({
                        ...oldReport,
                        updatedAt: new Date().toISOString(),
                        items: oldReport.items.filter(
                            (reportItem) => reportItem.id !== mutationVariables.id
                        )
                    });
                }
            );
        }
    });
}

export function useUpdateReportItem() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: ReportItemV2UpdateInput) => {
            const response = await request(ReportItemUpdate, {
                input: mapReportItemInputForApi(input)
            });
            const rawItem = response.reportV2?.reportItemUpdate;
            if (!rawItem) throw new Error('Report item not returned from api');
            return {
                ...rawItem,
                definitionV2: rawItem.definitionV2 as ReportItem['definitionV2']
            };
        },
        onSuccess: (updatedReportItem, mutationVariables) => {
            // update single report cache to include the updated report item
            queryClient.setQueryData<Report>(
                reportQueryKeys.report(mutationVariables.reportId),
                (oldReport) => {
                    if (!oldReport) return;
                    return new Report({
                        ...oldReport,
                        updatedAt: new Date().toISOString(),
                        items: oldReport.items.map((reportItem) => {
                            if (updatedReportItem.id !== reportItem.id) return reportItem;
                            return updatedReportItem as ReportItem;
                        })
                    });
                }
            );
        }
    });
}

//
// account admin bespoke endpoints
export function useSearchReports(input: ReportSearchInput, options?: {enabled: boolean}) {
    const request = useClientMainAuthorizedFetch();
    const enabled = options?.enabled ?? true;
    return useQuery({
        queryKey: ['userReports', input],
        queryFn: enabled
            ? async () => {
                  const response = await request(ReportSearch, {input});
                  const rawReports = response.reportV2?.reportSearch;
                  if (!rawReports) throw new Error('Reports not returned from api');
                  return rawReports.map((rawReport) => new Report(rawReport));
              }
            : skipToken
    });
}

// pdf reports

type pdfReportPayload = {
    reportId: string;
    timezone?: string;
    printConfig?: string;
};

type pdfCreativePayload = {
    timezone?: string;
    printConfig?: string;
    appUrl?: string;
    filter?: string;
};

export function usePdfReportDownload() {
    const request = useClientMainAuthorizedFetch();
    return useMutation({
        mutationFn: async (payload: pdfReportPayload) => {
            const pollingData = createPollingGenerator({
                createRequest: async () => {
                    const response = await request(PdfReportCreate, {
                        reportId: payload.reportId,
                        timezone: payload.timezone,
                        printConfig: payload.printConfig
                    });
                    return response.pdfRenderer?.pdfReportCreate;
                },
                pollRequest: async (createResponse) => {
                    if (createResponse?.downloadLink) {
                        const response = await fetch(createResponse?.downloadLink);
                        return {status: response.status, downloadUrl: createResponse};
                    }
                },
                pollWhile: (data) => data?.status === 404,
                interval: 2000
            });

            // Can side step this by flattening the whole request to one promise
            let lastPayload;
            for await (const payload of pollingData) {
                lastPayload = payload;
            }

            return {
                downloadLink: lastPayload?.downloadUrl?.downloadLink
            };
        }
    });
}

export function usePdfCreativeReport() {
    const request = useClientMainAuthorizedFetch();
    return useMutation({
        mutationFn: async (payload: pdfCreativePayload) => {
            const pollingData = createPollingGenerator({
                createRequest: async () => {
                    const response = await request(PdfCreativeProfile, {
                        appUrl: payload.appUrl ?? '',
                        filter: payload.filter ?? '',
                        timezone: payload.timezone ?? '',
                        printConfig: payload.printConfig
                    });

                    return response.pdfRenderer?.pdfCreative;
                },
                pollRequest: async (createResponse) => {
                    if (createResponse?.downloadLink) {
                        const response = await fetch(createResponse?.downloadLink);
                        return {status: response.status, downloadUrl: createResponse};
                    }
                },
                pollWhile: (data) => data?.status === 404,
                interval: 2000
            });

            // Can side step this by flattening the whole request to one promise
            let lastPayload;
            for await (const payload of pollingData) {
                lastPayload = payload;
            }

            return {
                downloadLink: lastPayload?.downloadUrl?.downloadLink
            };
        }
    });
}

//
// helpers

function updateReportsInListCache(data: {
    updatedReports: Report[];
    listInput: ReportListInput;
    queryClient: QueryClient;
}) {
    const {updatedReports, listInput, queryClient} = data;

    // update relevant reports in the single report cache
    updatedReports.forEach((updatedReport) => {
        queryClient.setQueryData<Report>(reportQueryKeys.report(updatedReport.id), (oldReport) => {
            return new Report({...oldReport, ...updatedReport});
        });
    });

    // update relevant reports from the list cache
    queryClient.setQueryData<{items: Report[]}>(
        reportQueryKeys.reportList(listInput),
        (oldCacheEntry) => {
            if (!oldCacheEntry) return;
            return {
                ...oldCacheEntry,
                items: oldCacheEntry?.items.map((oldReport) => {
                    const updatedReport = updatedReports.find(
                        (updated) => updated.id === oldReport.id
                    );
                    if (!updatedReport) return oldReport;
                    return new Report({...oldReport, ...updatedReport});
                })
            };
        }
    );
}
